import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

const MonthlyPurchases = () => {
  const [allPurchases, setAllPurchases] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const navigate = useNavigate();

  // Handle F3 key press for editing purchase bill
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F3' || e.code === 'F3' || e.which === 114) {
        if (selectedPurchase) {
          e.preventDefault();
          const billToEdit = selectedPurchase;
          setSelectedPurchase(null);
          navigate('/dashboard/purchases', { state: { editBillId: billToEdit.id, editBill: billToEdit } });
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPurchase, navigate]);

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const res = await fetch(apiUrl('/api/purchases'));
        if (res.ok) {
          const data = await res.json();
          setAllPurchases(data);
        }
      } catch (error) {
        console.error('Failed to fetch purchases', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPurchases();
  }, []);
  const purchasesData = allPurchases.filter(purchase => 
    purchase.date === selectedDate &&
    purchase.paymentType === 'Credit'
  );

  const handleDeletePurchase = async (purchaseId) => {
    if (!purchaseId) return;
    if (!window.confirm("Are you sure you want to delete this purchase bill?")) return;
    try {
      const res = await fetch(apiUrl(`/api/purchases/${purchaseId}`), { method: 'DELETE' });
      if (res.ok) {
        setAllPurchases(prev => prev.filter(p => p.id !== purchaseId));
        if (selectedPurchase?.id === purchaseId) setSelectedPurchase(null);
      } else {
        alert("Failed to delete purchase bill on server.");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting purchase: " + err.message);
    }
  };

  return (
    <div className="space-y-2 md:space-y-10 animate-in fade-in duration-700 p-2 md:p-8">
      <div className="flex items-center gap-2 mb-2 md:mb-8">
        <button 
          onClick={() => navigate('/dashboard')}
          className="p-2 bg-white rounded-xl shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
        </button>
        <div className="w-2 h-8 md:h-10 rounded-full bg-blue-500 shadow-sm"></div>
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800 tracking-wide">Purchase Report</h2>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="p-2 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:border-amber-500 transition-colors" />
        </div>
      </div>

      <div className="bg-white rounded-3xl p-3 md:p-8 border border-slate-200 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)]">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : purchasesData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse whitespace-nowrap">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-[9px] uppercase tracking-wider font-bold">
                  <th className="p-2 rounded-tl-xl">Date</th>
                  <th className="p-2">Party / Supplier Name</th>
                  <th className="p-2">Invoice No</th>
                  <th className="p-2">Payment</th>
                  <th className="p-2 text-right">Total Amount</th>
                  <th className="p-2 text-center rounded-tr-xl">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {purchasesData.map((purchase, index) => (
                  <tr key={index} className="hover:bg-blue-50/50 transition-colors cursor-pointer group" onClick={() => setSelectedPurchase(purchase)}>
                    <td className="p-2">
                      <div className="text-[9px] font-black text-slate-500">{purchase.date}</div>
                    </td>
                    <td className="p-2">
                      <div className="font-bold text-slate-800">{purchase.supplierName || 'Unknown Party'}</div>
                    </td>
                    <td className="p-2 font-black text-slate-600">
                      {purchase.invoiceNo || '-'}
                    </td>
                    <td className="p-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest ${purchase.paymentType === 'Cash' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'}`}>
                        {purchase.paymentType || 'Credit'}
                      </span>
                    </td>
                    <td className="p-2 text-right font-black text-slate-800">
                      ₹{purchase.netAmount?.toFixed(2) || (purchase.totalAmount && purchase.totalAmount.toFixed(2)) || '0.00'}
                    </td>
                    <td className="p-2 text-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePurchase(purchase.id);
                        }}
                        className="px-2 py-1 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white rounded-lg border border-rose-200 transition-colors text-xs font-bold flex items-center gap-1 mx-auto cursor-pointer"
                        title="Delete this purchase bill"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 text-slate-400 mb-2">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4M8 16l-4-4 4-4"></path></svg>
            </div>
            <p className="text-slate-500 font-bold">No purchases recorded this month yet.</p>
          </div>
        )}
      </div>

      {/* Purchase Details Modal */}
      {selectedPurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-white shrink-0">
              <h3 className="text-lg font-black text-blue-900 flex items-center gap-2">
                <div className="w-2 h-6 rounded-full bg-blue-400"></div>
                Purchase Details {selectedPurchase.invoiceNo && `#${selectedPurchase.invoiceNo}`}
              </h3>
              <button onClick={() => setSelectedPurchase(null)} className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              
              {/* Info Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Supplier / Party</p>
                  <p className="font-black text-slate-700 text-sm">{selectedPurchase.supplierName || 'Unknown'}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Payment Type</p>
                  <p className="font-black text-slate-700 text-sm">{selectedPurchase.paymentType || 'Credit'}</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-100 shadow-sm shadow-emerald-100">
                  <p className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-widest mb-1">Paid Amount</p>
                  <p className="font-black text-emerald-700 text-lg">₹{(selectedPurchase.paymentType === 'Credit' ? 0 : (selectedPurchase.netAmount || selectedPurchase.totalAmount || 0)).toFixed(2)}</p>
                </div>
                <div className="bg-rose-50 p-3 rounded-xl border border-rose-100 shadow-sm shadow-rose-100">
                  <p className="text-[10px] font-bold text-rose-600/70 uppercase tracking-widest mb-1">Pending Due</p>
                  <p className="font-black text-rose-700 text-lg">₹{(selectedPurchase.paymentType === 'Credit' ? (selectedPurchase.netAmount || selectedPurchase.totalAmount || 0) : 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Products Table */}
              <div>
                <h4 className="text-xs font-black text-slate-700 uppercase tracking-widest mb-2 flex items-center gap-2">
                  Products List
                  <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-[9px]">{selectedPurchase.items?.length || 0}</span>
                </h4>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left border-collapse whitespace-nowrap">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
                        <th className="p-3 border-b border-slate-200">Product Name</th>
                        <th className="p-3 border-b border-slate-200 text-center">Qty</th>
                        <th className="p-3 border-b border-slate-200 text-right">Purchase Rate</th>
                        <th className="p-3 border-b border-slate-200 text-right bg-teal-50/50 text-teal-800" title="Rate with GST included">Cost Rate</th>
                        <th className="p-3 border-b border-slate-200 text-right">MRP</th>
                        <th className="p-3 border-b border-slate-200 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedPurchase.items?.map((item, idx) => (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="p-3 text-xs font-black text-slate-700">{item.productName}</td>
                          <td className="p-3 text-xs font-black text-slate-700 text-center bg-slate-50/50">{item.qty}</td>
                          <td className="p-3 text-xs font-bold text-slate-600 text-right">₹{parseFloat(item.purchaseRate || item.rate || 0).toFixed(2)}</td>
                          <td className="p-3 text-xs font-black text-teal-700 text-right bg-teal-50/30">₹{(parseFloat(item.costRate || item.costPrice) || (parseFloat(item.rate || 0) * (1 + (parseFloat(item.gst || 0)/100)))).toFixed(2)}</td>
                          <td className="p-3 text-xs font-bold text-slate-500 text-right">₹{parseFloat(item.mrp || 0).toFixed(2)}</td>
                          <td className="p-3 text-sm font-black text-emerald-600 text-right">₹{parseFloat(item.amount || item.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      {!selectedPurchase.items?.length && (
                        <tr><td colSpan="6" className="p-4 text-center text-xs text-slate-400 font-bold">No products found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
               <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const billToEdit = selectedPurchase;
                    setSelectedPurchase(null);
                    navigate('/dashboard/purchases', { state: { editBillId: billToEdit.id, editBill: billToEdit } });
                  }}
                  className="px-3.5 py-1.5 bg-amber-50 hover:bg-amber-500 text-amber-800 hover:text-white border border-amber-300 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                  title="Edit this purchase bill (F3)"
                >
                  <span className="bg-amber-600 text-white px-1 py-0.2 rounded text-[10px] font-black">F3</span>
                  ✏️ Edit Bill
                </button>
                <button
                  type="button"
                  onClick={() => handleDeletePurchase(selectedPurchase.id)}
                 className="px-3.5 py-1.5 bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white border border-rose-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
               >
                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                 </svg>
                 Delete This Bill
                </button>
              </div>
               <div className="text-sm font-black text-slate-800">
                 Net Total: <span className="text-blue-600 text-xl ml-1">₹{(selectedPurchase.netAmount || selectedPurchase.totalAmount || 0).toFixed(2)}</span>
               </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyPurchases;
